import { useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  fetchProfileByUid,
  fetchProfileByUsername,
  updateProfile,
  uploadUserPhoto,
  uploadUserCoverPhoto,
  updateUserMartialArt,
  removeUserMartialArt,
  searchForUsers,
  fetchUserFriends,
  fetchUserFriendRequests,
  sendUserFriendRequest,
  acceptUserFriendRequest,
  declineUserFriendRequest,
  undoUserDeclinedFriendRequest,
  removeUserFriend,
  fetchSuggestedFriends,
  clearSearchResults,
  clearError,
  clearViewedProfiles
} from '../store/slices/userSlice';
import { UserProfileUpdateData } from '../services/firebase/profile.service';

export const useUser = () => {
  const dispatch = useAppDispatch();
  const {
    currentProfile,
    viewedProfiles,
    friendsMap,
    friendsList,
    friendRequests,
    searchResults,
    suggestedFriends,
    isLoading,
    error,
    pendingFriendRequests
  } = useAppSelector((state) => state.user);

  // Get profile by UID
  const getProfileByUid = useCallback(
    (uid: string) => {
      return dispatch(fetchProfileByUid(uid)).unwrap();
    },
    [dispatch]
  );

  // Alias for getProfileByUid for backward compatibility
  const getUserProfile = useCallback(
    (uid: string) => {
      return dispatch(fetchProfileByUid(uid)).unwrap();
    },
    [dispatch]
  );

  // Get profile by username
  const getProfileByUsername = useCallback(
    (username: string) => {
      return dispatch(fetchProfileByUsername(username)).unwrap();
    },
    [dispatch]
  );

  // Update profile
  const updateUserProfile = useCallback(
    (uid: string, data: UserProfileUpdateData) => {
      return dispatch(updateProfile({ uid, data })).unwrap();
    },
    [dispatch]
  );

  // Upload profile photo
  const uploadProfilePhoto = useCallback(
    (uid: string, file: File) => {
      return dispatch(uploadUserPhoto({ uid, file })).unwrap();
    },
    [dispatch]
  );

  // Upload cover photo
  const uploadCoverPhoto = useCallback(
    (uid: string, file: File) => {
      return dispatch(uploadUserCoverPhoto({ uid, file })).unwrap();
    },
    [dispatch]
  );

  // Update martial art
  const updateMartialArt = useCallback(
    (uid: string, martialArtId: string, data: {
      name: string;
      startDate: number;
      experience: number;
      rank?: string;
      instructor?: string;
    }) => {
      return dispatch(updateUserMartialArt({ uid, martialArtId, data })).unwrap();
    },
    [dispatch]
  );

  // Remove martial art
  const removeMartialArt = useCallback(
    (uid: string, martialArtId: string) => {
      return dispatch(removeUserMartialArt({ uid, martialArtId })).unwrap();
    },
    [dispatch]
  );

  // Search users
  const searchUsers = useCallback(
    (searchTerm: string, limit?: number) => {
      return dispatch(searchForUsers({ 
        searchTerm, 
        limit
      })).unwrap();
    },
    [dispatch]
  );

  // Get user friends
  const getUserFriends = useCallback(
    (uid: string) => {
      return dispatch(fetchUserFriends(uid)).unwrap();
    },
    [dispatch]
  );

  // Get friend requests
  const getFriendRequests = useCallback(
    (uid: string) => {
      return dispatch(fetchUserFriendRequests(uid)).unwrap();
    },
    [dispatch]
  );

  // Get pending friend requests (alias for getFriendRequests)
  const getPendingFriendRequests = useCallback(
    (uid: string) => {
      return dispatch(fetchUserFriendRequests(uid)).unwrap();
    },
    [dispatch]
  );

  // Send friend request
  const sendFriendRequest = useCallback(
    (fromUid: string, toUid: string) => {
      return dispatch(sendUserFriendRequest({ fromUid, toUid })).unwrap();
    },
    [dispatch]
  );

  // Accept friend request
  const acceptFriendRequest = useCallback(
    (currentUid: string, requestorUid: string) => {
      return dispatch(acceptUserFriendRequest({ currentUid, requestorUid })).unwrap();
    },
    [dispatch]
  );

  // Decline friend request
  const declineFriendRequest = useCallback(
    (currentUid: string, requestorUid: string) => {
      return dispatch(declineUserFriendRequest({ currentUid, requestorUid })).unwrap();
    },
    [dispatch]
  );

  // Undo declined friend request (accept a previously declined request)
  const undoDeclinedFriendRequest = useCallback(
    (currentUid: string, declinedUid: string) => {
      return dispatch(undoUserDeclinedFriendRequest({ currentUid, declinedUid })).unwrap();
    },
    [dispatch]
  );

  // Cancel friend request (alias for declineFriendRequest)
  const cancelFriendRequest = useCallback(
    (currentUid: string, requestorUid: string) => {
      return dispatch(declineUserFriendRequest({ currentUid, requestorUid })).unwrap();
    },
    [dispatch]
  );

  // Remove friend
  const removeFriend = useCallback(
    (currentUid: string, friendUid: string) => {
      return dispatch(removeUserFriend({ currentUid, friendUid })).unwrap();
    },
    [dispatch]
  );

  // Clear search results
  const clearSearch = useCallback(() => {
    dispatch(clearSearchResults());
  }, [dispatch]);

  // Clear error
  const clearUserError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Clear viewed profiles
  const clearViewed = useCallback(() => {
    dispatch(clearViewedProfiles());
  }, [dispatch]);

  // Get suggested friends
  const getSuggestedFriends = useCallback(
    (uid: string, limit?: number) => {
      return dispatch(fetchSuggestedFriends({ uid, limit })).unwrap();
    },
    [dispatch]
  );

  // Check if user is friend
  const isFriend = useCallback(
    (uid: string) => {
      return friendsList.includes(uid);
    },
    [friendsList]
  );

  // Get friend request status
  const getFriendRequestStatus = useCallback(
    (uid: string) => {
      if (friendRequests[uid]) {
        return friendRequests[uid].status;
      }
      return null;
    },
    [friendRequests]
  );

  return {
    // State
    currentProfile,
    viewedProfiles,
    friendsMap,
    friendsList,
    friendRequests,
    searchResults,
    suggestedFriends,
    isLoading,
    error,
    pendingFriendRequests,
    // Add users as an alias for viewedProfiles for backwards compatibility
    users: viewedProfiles,

    // Actions
    getProfileByUid,
    getUserProfile,
    getUserById: getUserProfile, // Alias for getUserProfile
    getProfileByUsername,
    updateUserProfile,
    uploadProfilePhoto,
    uploadCoverPhoto,
    updateMartialArt,
    removeMartialArt,
    searchUsers,
    getUserFriends,
    getFriendRequests,
    getPendingFriendRequests,
    sendFriendRequest,
    acceptFriendRequest,
    declineFriendRequest,
    undoDeclinedFriendRequest,
    cancelFriendRequest,
    removeFriend,
    getSuggestedFriends,
    clearSearch,
    clearUserError,
    clearViewed,

    // Helpers
    isFriend,
    getFriendRequestStatus
  };
};